import { GzSingleSelectComplexWebComponent } from '../component/GzSingleSelectComplexWebComponent';
import { GzSingleSelectComplexRepository } from '../repository/GzSingleSelectComplexRepository';
import { DEFAULT_DATA_LIMIT, GetItemsResponse, SingleSelectComplexEvents } from '../types';
import { ListEvents, ListItem } from '../../gzSingleSelectList/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';

export class GzSingleSelectController {
  private dependencies: any;
  public component: GzSingleSelectComplexWebComponent;
  public dataRepository: GzSingleSelectComplexRepository;

  private scrollError = new Error('Unable to process scroll');
  private loadedItems = 0;
  private _totalItemsCount: number;
  private _filteredTotalItemsCount: number;
  private _pageCount = 1;
  private _filteredPageCount = 1;
  private defaultItems: ListItem[];
  private _extraDetailsForRequest: any;

  constructor(dependencies, component: GzSingleSelectComplexWebComponent) {
    if (this.dependencies) {
      return;
    }

    this.dependencies = dependencies;
    this.buildDataRepository();
    this.component = component;
    this.addEventListeners();
  }

  get totalItemsCount(): number | undefined {
    if (this.hasSearchString()) {
      return this._filteredTotalItemsCount;
    }
    return this._totalItemsCount;
  }

  set totalItemsCount(value: number | undefined) {
    if (!value) {
      return;
    }
    if (this.hasSearchString()) {
      this._filteredTotalItemsCount = value;
      return;
    }
    this._totalItemsCount = value;
  }

  get pageCount(): number {
    if (this.hasSearchString()) {
      return this._filteredPageCount;
    }
    return this._pageCount;
  }

  set pageCount(value: number) {
    if (this.hasSearchString()) {
      this._filteredPageCount = value;
      return;
    }
    this._pageCount = value;
  }

  set extraDetailsForRequest(value) {
    this._extraDetailsForRequest = value;
  }

  get extraDetailsForRequest() {
    return this._extraDetailsForRequest;
  }

  private hasSearchString(): boolean {
    return this.getSearchString() !== '';
  }

  private getSearchString(): string {
    return this.component.selectList?.searchInput?.value || '';
  }

  private loadedAllItems(): boolean {
    const pageLimit = this.component.pageLimit || DEFAULT_DATA_LIMIT;
    const shouldWorkWithoutTotalCount = this.dataRepository.shouldWorkWithoutTotalCount();
    // This modification originates with CompanyFilterController.loadedAllItems, because we can't do a total item count in some cases.
    // See https://bitdefender.atlassian.net/browse/HELIX-11877 and https://bitdefender.atlassian.net/browse/CERES-50639
    const itemsToBeCompared = shouldWorkWithoutTotalCount ? this.loadedItems : this.totalItemsCount;
    const moreItemsToLoad = (itemsToBeCompared as number) < this.pageCount * pageLimit;
    return moreItemsToLoad || this.loadedItems === this.totalItemsCount;
  }

  private buildDataRepository(): void {
    this.dataRepository = new GzSingleSelectComplexRepository();
    this.dataRepository.injectDependencies(this.dependencies);
  }

  addEventListeners(): void {
    this.component.addEventListener('close', this.handleDropdownClose.bind(this));
    this.component.addEventListener(ListEvents.SCROLL, this.loadMoreOptions.bind(this));
    this.component.addEventListener(ListEvents.SEARCH_INPUT_CHANGE, this.getItems.bind(this));
  }

  public clearState(): void {
    this.loadedItems = 0;
    this._totalItemsCount = 0;
    this._filteredTotalItemsCount = 0;
    this._pageCount = 1;
    this._filteredPageCount = 1;
    this.defaultItems = [];
  }

  async loadMoreOptions(event: CustomEvent): Promise<void> {
    const pageLimit = this.component.pageLimit || DEFAULT_DATA_LIMIT;
    const haltLoadMore = this.component.isLoading || this.loadedAllItems();
    if (haltLoadMore) {
      return;
    }

    if (!this.component.selectList.shadowRoot) {
      throw this.scrollError;
    }

    if (
      Math.ceil(event.detail.scrollTop + event.detail.clientHeight) + 2 < event.detail.scrollHeight ||
      event.detail.scrollTop === 0
    ) {
      return;
    }

    try {
      const searchString = this.getSearchString();
      this.component.isLoading = true;
      this.pageCount += 1;

      // repository call
      const data = await this.dataRepository.getItems({
        searchValue: searchString,
        limit: pageLimit,
        page: this.pageCount,
        extra: this.extraDetailsForRequest,
        context: this.component.filterContext,
      });

      this.component.isLoading = false;

      if (!data) {
        throw new Error('Unable to query for items');
      }
      await this.awaitListRerender();
      const uniqueItems = this.removeDuplicateItems(data.items);

      if (!uniqueItems.length) {
        return this.loadMoreOptions(event);
      }

      this.component.filterOptions = this.component.filterOptions.concat(uniqueItems);
      await this.awaitListRerender();
      this.component.selectList.setListScrollPosition(event.detail.scrollTop);
      this.loadedItems = this.component.filterOptions.length;
    } catch (e) {
      this.pageCount -= 1;
      this.component.isLoading = false;
      this.component.isError = true;
      console.error(e);
    }
  }

  async onClearSearchBar(event: CustomEvent): Promise<void> {
    try {
      this._filteredPageCount = 1;
      this._pageCount = 1;
      const pageLimit = this.component.pageLimit || DEFAULT_DATA_LIMIT;

      if (isEmpty(this.defaultItems)) {
        this.component.isLoading = true;
        // repository call
        const data = await this.dataRepository.getInitialItems({
          context: this.component.filterContext,
          limit: pageLimit,
          page: 1,
          extra: this.extraDetailsForRequest,
        });

        this.component.isLoading = false;

        if (!data) {
          throw new Error('Unable to query for items');
        }
        await this.awaitListRerender();
        this.defaultItems = data.items;
        this.totalItemsCount = data.totalItemsCount;
      }

      const selectedOption = this.component.getSelectedOption();
      const defaultListContainsSelectedOption =
        this.defaultItems.find(item => item.value === selectedOption?.value) !== undefined;
      if (selectedOption !== null && !defaultListContainsSelectedOption) {
        this.component.filterOptions = this.defaultItems.concat(selectedOption);
      } else {
        this.component.filterOptions = this.defaultItems;
      }

      await this.awaitListRerender();
      this.loadedItems = this.component.filterOptions.length;
    } catch (e) {
      this.pageCount -= 1;
      this.component.isLoading = false;
      this.component.isError = true;
      console.error(e);
    }
  }

  async getItems(event: CustomEvent): Promise<void> {
    const searchString = event.detail.value;
    if (searchString === '') {
      return await this.onClearSearchBar(event);
    }

    try {
      this.component.isLoading = true;
      this._filteredPageCount = 1;

      const pageLimit = this.component.pageLimit || DEFAULT_DATA_LIMIT;
      const data = await this.dataRepository.getItems({
        searchValue: event.detail.value,
        limit: pageLimit,
        page: 1,
        extra: this.extraDetailsForRequest,
        context: this.component.filterContext,
      });

      this.component.isLoading = false;

      if (!data) {
        throw new Error('Unable to query for items');
      }

      await this.awaitListRerender();
      this.totalItemsCount = data.totalItemsCount;
      this.component.filterOptions = data.items;

      await this.awaitListRerender();
      this.loadedItems = this.component.filterOptions.length;
    } catch (e) {
      this.component.isLoading = false;
      this.component.isError = true;
      console.error(e);
    }
  }

  public async setFilterOptions(itemsInfo: GetItemsResponse | undefined, preventBackendCall = false): Promise<void> {
    if (itemsInfo && itemsInfo.items && itemsInfo.items.length) {
      this.component.filterOptions = itemsInfo.items;
    }
    if (itemsInfo && itemsInfo.context) {
      this.component.filterContext = itemsInfo.context;
    }

    if (!preventBackendCall) {
      try {
        const pageLimit = this.component.pageLimit || DEFAULT_DATA_LIMIT;
        this.component.isLoading = true;
        // repository call
        itemsInfo = await this.dataRepository.getInitialItems({
          context: itemsInfo?.context,
          limit: pageLimit,
          page: 1,
          value: this.component.value,
          extra: this.extraDetailsForRequest,
        });

        this.component.isLoading = false;

        if (!itemsInfo) {
          throw new Error('Unable to query for items');
        }
        await this.awaitListRerender();
        this.defaultItems = itemsInfo.items;
        this.totalItemsCount = itemsInfo.totalItemsCount;
      } catch (e) {
        this.pageCount -= 1;
        this.component.isLoading = false;
        this.component.isError = true;
        console.error(e);
        return;
      }
    }

    if (!itemsInfo || !itemsInfo.hasOwnProperty('items') || !itemsInfo.hasOwnProperty('totalItemsCount')) {
      throw new Error('Incorrect items info provided');
    }

    if (itemsInfo.items.length > 0) {
      this.component.filterOptions = this.component.filterOptions.concat(this.removeDuplicateItems(itemsInfo.items));
      await this.awaitListRerender();
    }

    this.defaultItems = this.component.filterOptions;
    this.totalItemsCount = itemsInfo.totalItemsCount;
    this.pageCount = 1;
    this.loadedItems = this.component.filterOptions.length;

    this.announceBackendOptionsLoaded();
  }

  private announceBackendOptionsLoaded(): void {
    const detailedFilterOptions = this.component.filterOptions.map(filterOption => {
      return {
        ...filterOption,
        selected: filterOption.value === this.component.value,
      };
    });

    const event = new CustomEvent(`${this.component.filterType}_${SingleSelectComplexEvents.BACKEND_OPTIONS_LOADED}`, {
      detail: {
        filterOptions: detailedFilterOptions,
      },
      bubbles: true,
      composed: true,
    });
    this.component.dispatchEvent(event);
  }

  /**
   * Filter out new data items that are already in the selector
   * @param {ListItem[]} dataListItems
   * @returns {ListItem[]}
   */
  protected removeDuplicateItems(dataListItems: ListItem[]): ListItem[] {
    return dataListItems.filter(
      listItem => !this.component.filterOptions.filter(filterOption => filterOption.value === listItem.value).length
    );
  }

  /**
   * isError may be reset if there are any loaded items
   * @returns {Promise<void>}
   * @private
   */
  private async handleDropdownClose(): Promise<void> {
    if (this.component.isError && this.loadedItems !== 0) {
      this.component.isError = false;
    }
  }

  private async awaitListRerender(): Promise<void> {
    await this.component.updateComplete;
    await this.component.selectList.updateComplete;
  }
}
